<script setup lang="ts">
import { onMounted, useTemplateRef } from "vue";

import { useEventBus } from "@/composables/useEventBus";
import { fakeZoom } from "@/model/Settings";
import color from "@/model/color.module.scss";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useZoomStore } from "@/store/zoom";
import { boundsRect } from "@/utils/dom/coordinates";
import { clearStyles, getStyleInfo } from "@/utils/dom/styleCache";
import type { PaintFunctions } from "@/utils/text/PaintLayer";
import { registerPaintLayer } from "@/utils/text/PaintLayer";
import type { Line } from "@/utils/text/lineBreaker";

const canvasRef = useTemplateRef("canvas"); // TODO could we use OffscreenCanvas instead?

let ctx!: CanvasRenderingContext2D;
const charWidthCache = new Map<string, number>();

const { onSetting } = useEventBus();

onMounted(() => {
  registerPaintLayer({ init });
  ctx = canvasRef.value!.getContext("2d")!;
  onSetting("stickyFont", () => {
    charWidthCache.clear();
    clearStyles();
  });
});

function init(elem: SVGElement | HTMLElement, scale: RelativeCoordinate): PaintFunctions | null {
  const style = getStyleInfo(elem);
  const { size: elemFontSize, unit: elemFontUnit } = parseFont(elem);

  const canvas = canvasRef.value!;
  const unscaledCanvasUnitsPerPixel = canvas.width / canvas.offsetWidth;

  const zoomFactor = fakeZoom / useZoomStore().factor;
  const sizes = calcElementSize(elem);
  if (!sizes) {
    return null;
  }
  const { elementScale, canvasUnitsPerPixel } = sizes;
  const elementWidth = sizes.boxWidth * scale.x;
  const elementHeight = sizes.boxHeight * scale.y;

  const pixelPerFontUnit = measurePixelPerFontUnit(
    parseFloat(elemFontSize),
    style.minFontSize,
    style.maxFontSize,
  );

  const makeVisible = () => {
    const elStyle = canvas.style;
    elStyle.visibility = "visible";
    elStyle.zIndex = "3";
  };

  function parseFont(elem: SVGElement | HTMLElement) {
    const style = elem.style.fontSize;
    if (style) {
      const [input, size, unit] = /([0-9.]+)(.*)/.exec(style) || [];
      if (!input) {
        throw Error(`unparseable font size '${style}'`);
      }
      return { size, unit };
    }
    const attr = elem.getAttribute("font-size");
    if (attr) {
      return { size: attr, unit: "px" };
    }
    throw Error("unparseable font size: neither style.fontSize nor font-size attribute found.");
  }

  function calcElementSize(elem: SVGElement | HTMLElement) {
    if (elem instanceof SVGElement) {
      const svg = findSvgRoot(elem);
      if (!svg.clientWidth) {
        return null;
      }
      const svgUnitsPerPixel = svg.clientWidth / +svg.getAttribute("width")!;
      const width = +elem.getAttribute("width")!;
      const height = +elem.getAttribute("height")!;

      return {
        elementScale: 1,
        boxWidth: (width / zoomFactor) * svgUnitsPerPixel,
        boxHeight: (height / zoomFactor) * svgUnitsPerPixel,
        canvasUnitsPerPixel: unscaledCanvasUnitsPerPixel * svgUnitsPerPixel,
      };
    }

    // offsetWidth does not take into account "CSS transformation: scale", but getBoundingClientRect does.
    // getBoundingClientRect has higher precision but to use it, the possible scalings have to be inversed.
    // there are 3 possible CSS scalings: fakeZoom, InputText's editZoom and board zoom.
    const rect = boundsRect(elem);
    return {
      elementScale: zoomFactor * (rect.size.x / elem.offsetWidth),
      boxWidth: rect.size.x,
      boxHeight: rect.size.y,
      canvasUnitsPerPixel: unscaledCanvasUnitsPerPixel,
    };
  }

  function findSvgRoot(el: SVGElement): SVGElement {
    while (el.nodeName !== "svg") {
      el = el.parentElement as unknown as SVGElement;
    }
    return el;
  }

  function setElementFontSize(size: number) {
    elem.style.fontSize = size + elemFontUnit;
  }

  function measurePixelPerFontUnit(fontSize: number, minFontSize: number, maxFontSize: number) {
    setElementFontSize(fontSize);
    const elemStyle = window.getComputedStyle(elem);
    let pixel = parseFloat(elemStyle.fontSize);
    // getComputedStyle may respect browser's minimum/maximum font size
    // therefore, adjust font size if it's outside the limits
    if (pixel <= minFontSize || pixel >= maxFontSize) {
      const fontSizeFactor = 100 / pixel;
      setElementFontSize(fontSize * fontSizeFactor);
      pixel = parseFloat(elemStyle.fontSize) / fontSizeFactor;
      setElementFontSize(fontSize);
    }
    return pixel / fontSize;
  }

  function measureStringWidth(s: string): number {
    return ctx.measureText(s).width;
  }

  // We need the exact height for font size calculations
  const exactHeightInCanvas = elementHeight * unscaledCanvasUnitsPerPixel * zoomFactor;

  let canvasFontSize = 0;
  let canvasFont = "";

  function charWidth(char1: string, char2?: string): number {
    const s = char1 + (char2 || "");
    const key = canvasFont + style.letterSpacing + s;
    const val = charWidthCache.get(key);
    if (val !== undefined) {
      return val * canvasFontSize;
    }
    const width = char2
      ? measureStringWidth(s) - charWidth(char1) - charWidth(char2)
      : measureStringWidth(char1);
    charWidthCache.set(key, width / canvasFontSize);
    return width;
  }

  return {
    lineHeight: 0,
    widthInCanvas: Math.floor(elementWidth * unscaledCanvasUnitsPerPixel * zoomFactor),
    heightInCanvas: Math.floor(exactHeightInCanvas),

    setCanvasFont(size: number) {
      const pixelSize = size * pixelPerFontUnit;
      this.lineHeight = pixelSize * style.lineHeightFactor;
      canvasFontSize = pixelSize * elementScale * canvasUnitsPerPixel;
      canvasFont = `${style.fontWeight} ${style.fontFamily}`;
      ctx.letterSpacing = style.letterSpacing * canvasFontSize + "px";
      // NB: fontWeight is not working on chrome
      ctx.font = `${style.fontWeight} ${canvasFontSize}px ${style.fontFamily}`;
    },

    setElementFontSize,

    // so that line height = height in canvas
    get fontSizeForOneLine() {
      return (
        exactHeightInCanvas /
        (elementScale * canvasUnitsPerPixel * pixelPerFontUnit * style.lineHeightFactor)
      );
    },

    charsWidth(chars: string[], from: number, to: number): number {
      // measureText is relatively faster the longer the text gets, it's also more exact
      if (to - from >= 8) {
        const text = chars.slice(from, to).join("");
        return ctx.measureText(text).width;
      }

      let width = 0;
      // widths of single characters
      for (let i = from; i < to; i++) {
        width += charWidth(chars[i]);
      }
      // plus width diff of character pairs (e.g. 'AV' takes less space than a 'A' and a 'V')
      for (let i = from; i < to - 1; i++) {
        width += charWidth(chars[i], chars[i + 1]);
      }
      return width;
    },

    displayLines(x: number, y: number, text: string, lines: Line[]) {
      makeVisible();
      ctx.clearRect(x, y, this.widthInCanvas, this.heightInCanvas * 2);
      ctx.strokeStyle = color.textPrimary;
      ctx.strokeRect(x, y, this.widthInCanvas, this.heightInCanvas);
      ctx.strokeRect(
        x,
        y + lines.length * this.lineHeight * canvasUnitsPerPixel * elementScale,
        this.widthInCanvas,
        3,
      );
      let i = 0;
      for (const [startPos, endPos] of lines) {
        ctx.fillText(
          text.substring(startPos, endPos),
          x,
          y + canvasFontSize * (1 + style.lineHeightFactor * i),
        );
        i++;
      }
    },
  };
}
</script>

<template>
  <canvas
    id="paint-canvas"
    ref="canvas"
    width="6400"
    height="3600"
    aria-hidden="true"
    tabindex="-1"
  />
</template>

<style lang="scss">
@use "@/styles/variables";

#paint-canvas {
  visibility: hidden;
  position: absolute;
  pointer-events: none;
  width: 100% * variables.$fake-zoom;
  height: 100% * variables.$fake-zoom;
  font-size: 1rem;
}
</style>
